import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { hitApi, dateFormatter, commonToast, assetsUrlPath } from "../../../components/commonAction";

export default function AffiliateDetail({ pageTitle }) {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rateOverride, setRateOverride] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => {
        setLoading(true);
        hitApi({ url: `admin/affiliates/${id}`, method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) {
                setData(resp.data || null);
                setRateOverride(resp.data?.affiliate?.commissionRateOverride ?? "");
            }
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [id]);

    const update = (body) => {
        setBusy(true);
        hitApi({ url: `admin/affiliates/${id}`, method: "PATCH", data: body, alert: false }, (resp) => {
            setBusy(false);
            if (resp?.status) { commonToast("success", resp.message); load(); }
        });
    };

    if (loading) return <div className="pxl_rightBar_assets">Loading…</div>;
    if (!data?.affiliate) return <div className="pxl_rightBar_assets">Not found.</div>;

    const aff = data.affiliate;
    const user = data.user;

    const statusPill = aff.status === "approved"
        ? <span className="pxl-pill pxl-pill--success">approved</span>
        : aff.status === "suspended"
            ? <span className="pxl-pill pxl-pill--danger">suspended</span>
            : <span className="pxl-pill pxl-pill--muted">pending</span>;

    return (
        <div className="pxl_rightBar_assets">
            <Helmet><title>{pageTitle}</title></Helmet>

            {/* Page header */}
            <div className="pxl-rb-header-wrap">
                <div className="pxl-headingTop">
                    <div className="pxl-ud-heading-row">
                        <Link to="/admin/affiliates" className="pxl-ud-back-btn">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Affiliates
                        </Link>
                        <h2 className="pxl-rb-header-heading">Affiliate detail</h2>
                    </div>
                </div>
            </div>

            {/* User card + Profile + Earnings — 3 columns */}
            <div className="row g-3">

                {/* User card */}
                <div className="col-md-3">
                    <div className="pxl-panel h-100 pxl-aff-user-card">
                        <div className="pxl-aff-user-card-inner">
                            {user?.profilePic
                                ? <img src={assetsUrlPath(user.profilePic)} alt={user?.name} className="pxl-aff-user-avatar" />
                                : <div className="pxl-aff-user-avatar pxl-aff-user-avatar--initials">
                                    {user?.name ? user.name.charAt(0).toUpperCase() : "?"}
                                  </div>
                            }
                            <div className="pxl-aff-user-name">{user?.name || "—"}</div>
                            <div className="pxl-aff-user-email">{user?.email || "—"}</div>
                            <div style={{ marginTop: 4 }}>{statusPill}</div>
                            <Link
                                to={`/admin/user-details/${aff.userId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pxlCommon-btn pxlCommon-btn--outline"
                                style={{ marginTop: 16, width: "100%", textAlign: "center", display: "block" }}
                            >
                                View user details
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Profile */}
                <div className="col-md-5">
                    <div className="pxl-panel h-100">
                        <div className="pxl-panel-header">Profile</div>
                        <div className="pxl-panel-body">
                            <div className="row g-3">
                                <div className="col-sm-6">
                                    <div className="pxl-detail-item">
                                        <span className="pxl-detail-label">Referral code</span>
                                        <code className="pxl-detail-value" style={{ color: "#7e1acc", fontWeight: 600 }}>{aff.referralCode}</code>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="pxl-detail-item">
                                        <span className="pxl-detail-label">Status</span>
                                        <span className="pxl-detail-value">{statusPill}</span>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="pxl-detail-item">
                                        <span className="pxl-detail-label">PayPal email</span>
                                        <span className="pxl-detail-value">{aff.paypalEmail || <span style={{ color: "#8a8fa6" }}>Not set</span>}</span>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="pxl-detail-item">
                                        <span className="pxl-detail-label">Joined</span>
                                        <span className="pxl-detail-value">{dateFormatter(aff.createdAt)}</span>
                                    </div>
                                </div>
                                {aff.approvedAt && (
                                    <div className="col-sm-6">
                                        <div className="pxl-detail-item">
                                            <span className="pxl-detail-label">Approved</span>
                                            <span className="pxl-detail-value">{dateFormatter(aff.approvedAt)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="pxl-panel h-100">
                        <div className="pxl-panel-header">Earnings</div>

                        <div className="pxl-panel-body">
                            <div className="pxl-stat-row">
                                <span className="pxl-stat-label">Lifetime earnings</span>
                                <span className="pxl-stat-value">${Number(aff.totalEarnings || 0).toFixed(2)}</span>
                            </div>
                            <div className="pxl-stat-row">
                                <span className="pxl-stat-label">Lifetime payouts</span>
                                <span className="pxl-stat-value">${Number(aff.totalPaidOut || 0).toFixed(2)}</span>
                            </div>
                            <div className="pxl-stat-row" style={{ marginBottom: 0 }}>
                                <span className="pxl-stat-label">Current balance</span>
                                <span className="pxl-stat-value" style={{ color: Number(aff.currentBalance) < 0 ? "#d04050" : "#059669", fontWeight: 700 }}>
                                    ${Number(aff.currentBalance || 0).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="pxl-panel mt-3">
                <div className="pxl-panel-header">Actions</div>
                <div className="pxl-panel-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-sm-auto">
                            <p className="pxl-detail-label mb-2">Change status</p>
                            <div className="d-flex gap-2 flex-wrap">
                                {aff.status !== "approved" && (
                                    <button className="pxlCommon-btn pxlCommon-btn--success" disabled={busy} onClick={() => update({ status: "approved" })}>Approve</button>
                                )}
                                {aff.status !== "suspended" && (
                                    <button className="pxlCommon-btn pxlCommon-btn--danger" disabled={busy} onClick={() => update({ status: "suspended" })}>Suspend</button>
                                )}
                                {aff.status !== "pending" && (
                                    <button className="btn btn-light" disabled={busy} onClick={() => update({ status: "pending" })}>Reset to pending</button>
                                )}
                            </div>
                        </div>
                        <div className="col-sm-auto" style={{ borderLeft: "1px solid #e8eaf2", paddingLeft: 24 }}>
                            <p className="pxl-detail-label mb-2">Commission rate override <span style={{ fontWeight: 400 }}>(0–1; blank = global)</span></p>
                            <div className="d-flex gap-2 align-items-center">
                                <input
                                    type="number" step="0.01" min="0" max="1"
                                    value={rateOverride}
                                    onChange={e => setRateOverride(e.target.value)}
                                    style={{ width: 120, padding: "7px 10px", borderRadius: 6, border: "1px solid #e3e7f0", fontSize: 13 }}
                                    placeholder="e.g. 0.15"
                                />
                                <button
                                    className="pxlCommon-btn" disabled={busy}
                                    onClick={() => update({ commissionRateOverride: rateOverride === "" ? null : Number(rateOverride) })}
                                >
                                    Save rate
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent commissions */}
            <div className="pxl-panel mt-3">
                <div className="pxl-panel-header">Recent commissions</div>
                <div className="pxl-panel-body pxl-panel-body--flush">
                    {(data.recentCommissions || []).length === 0 ? (
                        <div className="text-muted" style={{ padding: 24, textAlign: "center" }}>No commission events yet.</div>
                    ) : (
                        <div className="pxl_table table-responsive">
                            <table className="pxl_table">
                                <thead>
                                    <tr><th>When</th><th>Type</th><th>Amount</th><th>Rate</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {data.recentCommissions.map(c => (
                                        <tr key={c._id}>
                                            <td><p>{dateFormatter(c.createdAt)}</p></td>
                                            <td>
                                                {c.type === "earned"
                                                    ? <span className="pxl-pill pxl-pill--success">earned</span>
                                                    : <span className="pxl-pill pxl-pill--warning">clawed back</span>}
                                            </td>
                                            <td><p style={{ color: Number(c.amount) < 0 ? "#d04050" : undefined }}>{c.currency} {Number(c.amount).toFixed(2)}</p></td>
                                            <td><p>{(Number(c.rate) * 100).toFixed(0)}%</p></td>
                                            <td>
                                                {c.status === "paid"
                                                    ? <span className="pxl-pill pxl-pill--info">paid</span>
                                                    : c.status === "available"
                                                        ? <span className="pxl-pill pxl-pill--info">available</span>
                                                        : <span className="pxl-pill pxl-pill--muted">pending</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Recent referral clicks */}
            <div className="pxl-panel mt-3">
                <div className="pxl-panel-header">Recent referral clicks</div>
                <div className="pxl-panel-body pxl-panel-body--flush">
                    {(data.recentClicks || []).length === 0 ? (
                        <div className="text-muted" style={{ padding: 24, textAlign: "center" }}>No clicks logged.</div>
                    ) : (
                        <div className="pxl_table table-responsive">
                            <table className="pxl_table">
                                <thead>
                                    <tr><th>When</th><th>Landing page</th><th>IP</th><th style={{ textAlign: "left" }}>Converted?</th></tr>
                                </thead>
                                <tbody>
                                    {data.recentClicks.map(c => {
                                        const ip = c.ipAddress;
                                        const isLoopback = !ip || ip === "::1" || ip === "127.0.0.1";
                                        return (
                                        <tr key={c._id}>
                                            <td><p>{dateFormatter(c.createdAt)}</p></td>
                                            <td><p><code style={{ fontSize: 12 }}>{c.landingPath || "/"}</code></p></td>
                                            <td><p style={{ color: "#8a8fa6", fontSize: 12 }}>{isLoopback ? "—" : ip}</p></td>
                                            <td style={{ textAlign: "left" }}>{c.convertedToUserId ? <span className="pxl-pill pxl-pill--success">yes</span> : <span style={{ color: "#8a8fa6" }}>—</span>}</td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
